import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ComponentProps } from "react";
import Button, { IconButton } from "../button/button";
import X from "../icons/x";

export default function Dialog({
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

export function DialogTrigger({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export function DialogPortal({
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

export function DialogClose({
  asChild,
  children,
  ...props
}: ComponentProps<typeof IconButton>) {
  if (asChild) {
    return <DialogPrimitive.Close asChild>{children}</DialogPrimitive.Close>;
  }

  return (
    <DialogPrimitive.Close asChild>
      <IconButton
        size="sm"
        variant="ghost"
        aria-label="Close"
        className="absolute top-2 right-2"
        {...props}
      >
        {children ?? <X />}
      </IconButton>
    </DialogPrimitive.Close>
  );
}

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  width,
  children,
  className,
  classNames,
  overlayProps,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  /** Tailwind max-width class in place of the default sm:max-w-lg */
  width?: string;
  classNames?: {
    root?: string;
    content?: string;
  };
  overlayProps?: ComponentProps<typeof DialogPrimitive.Overlay>;
}) {
  return (
    <DialogPortal>
      <DialogOverlay {...overlayProps} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // the same frame as the settings cards: soft border, xl corners,
          // the page's own surface, lifted by a deeper shadow
          "bg-background border-border text-foreground fixed top-[50%] left-[50%] z-50 grid max-h-[80vh] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-xl border shadow-xl duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "flex flex-col",
          width ?? "sm:max-w-lg",
          className,
          classNames?.root,
        )}
        {...props}
      >
        <div
          className={cn(
            "relative isolate flex flex-1 flex-col gap-5 overflow-y-auto p-5",
            classNames?.content,
          )}
        >
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-1 text-left", className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        // small buttons at the right, the main one last
        "flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-base leading-tight font-semibold", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-foreground-pressed text-sm", className)}
      {...props}
    />
  );
}

export function DialogAction(props: ComponentProps<typeof Button>) {
  return <Button size="sm" {...props} />;
}

// cancel stays quiet next to the main action
export function DialogCancel(props: ComponentProps<typeof Button>) {
  return <Button variant="ghost" size="sm" {...props} />;
}
